import { components } from "react-select";
import Form from "react-bootstrap/Form";
import { Placement } from "react-bootstrap/esm/types";
import classNames from "classnames";
import { SelectOption } from "@idw-types/common";
import { ReactSelectWrapper } from "../react-select-wrapper/react-select-wrapper";
import { IdwFormLabelWithIconContainer } from "../idw-form-label-with-icon-container/idw-form-label-with-icon-container";
import { cloneElement, useMemo } from "react";
import { LabelWidthTypes } from "@enums/label-width-types";
import { LayoutTypes } from "@enums/layout-types";
import { ReactTinyVirtualListWrapper } from "@containers/common/react-tiny-virtual-list-wrapper/react-tiny-virtual-list-wrapper";
import { ClickableIcon } from "../clickable-icon/clickable-icon";
import { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import styles from "./select-form-group.less";

type SelectFormGroupProps = {
    controlId: string;
    labelWidth?: LabelWidthTypes;
    className?: string;
    automationId: string;
    ariaLabel: string;
    label?: string;
    options: Array<SelectOption>;
    onChange: (option: SelectOption) => void;
    layout?: LayoutTypes;
    selectedOption: SelectOption;
    disabled: boolean;
    readOnly?: boolean;
    isLoading?: boolean;
    isRequired: boolean;
    isSearchable: boolean;
    isClearable: boolean;
    placeholder?: string;
    noOptionsMessage?: () => React.ReactNode;
    afterLabelTooltipText?: string;
    afterLabelTooltipTextPosition?: Placement;
    afterLabelIcon?: IconDefinition;
    afterLabelIconDisabled?: boolean;
    afterLabelIconOnClick?: () => void;
    afterInputElement?: React.ReactNode;
    afterInputIcon?: IconDefinition;
    afterInputIconDisabled?: boolean;
    afterInputIconOnClick?: () => void;
    rightAlignedLabelElement?: React.ReactNode;
    useVirtualList?: boolean;
};

const SelectFormGroupInput = (props: any) => {
    return cloneElement(<components.Input {...props} />, { "data-lpignore": "true" });
};

const SelectFormGroupOption = (props: any) => {
    const { onMouseMove, onMouseOver, ...rest } = props.innerProps;
    const newProps = { ...props, innerProps: rest };
    return (
        <components.Option {...newProps} className={styles.selectOption} />
    );
};

const SelectFormGroupMenuList = (props: any) => {
    return (
        <ReactTinyVirtualListWrapper
            {...props}
        />
    );
};


export const SelectFormGroup = (props: SelectFormGroupProps) => {
    const {
        controlId,
        labelWidth,
        className,
        automationId,
        ariaLabel,
        label,
        options,
        onChange,
        layout,
        selectedOption,
        disabled,
        readOnly,
        isLoading,
        isRequired,
        isSearchable,
        isClearable,
        placeholder,
        noOptionsMessage,
        afterLabelIcon,
        afterLabelIconDisabled,
        afterLabelIconOnClick,
        afterLabelTooltipText,
        afterLabelTooltipTextPosition,
        afterInputElement,
        afterInputIcon,
        afterInputIconDisabled,
        afterInputIconOnClick,
        rightAlignedLabelElement,
        useVirtualList
    } = props;

    const formGroupClassName = useMemo(() => {
        return layout === LayoutTypes.COLUMN ? styles.selectFormGroupColumn : styles.selectFormGroupRow;
    }, [layout]);

    const components = readOnly ? {
        //@ts-ignore
        Menu: () => null,
        //@ts-ignore
        MenuList: () => null,
    } : {
        Input: SelectFormGroupInput,
        MenuList: SelectFormGroupMenuList,
        Option: SelectFormGroupOption
    };

    if (!useVirtualList && !readOnly) {
        delete components.MenuList;
    }

    const customStyles = {
        option: (provided: any, state: { data: SelectOption }) => ({
            ...provided,
            color: state.data.color || undefined,
            fontStyle: state.data.fontStyle || undefined
        }),
        control: (provided: any) => ({
            ...provided,
            height: "2rem"
        })
    };

    return (
        <Form.Group controlId={controlId} className={classNames(formGroupClassName, className)}>
            {label && (
                <IdwFormLabelWithIconContainer
                    controlId={controlId}
                    labelWidth={labelWidth}
                    layout={layout}
                    label={label}
                    isRequired={isRequired}
                    afterLabelTooltipText={afterLabelTooltipText}
                    afterLabelTooltipTextPosition={afterLabelTooltipTextPosition}
                    afterLabelIcon={afterLabelIcon}
                    afterLabelIconDisabled={afterLabelIconDisabled}
                    afterLabelIconOnClick={afterLabelIconOnClick}
                    rightAlignedLabelElement={rightAlignedLabelElement}
                />
            )}
            <div className={styles.selectContainer}>
                <Form.Control
                    as={ReactSelectWrapper}
                    // @ts-ignore
                    custom
                    styles={customStyles}
                    isRequired={isRequired}
                    menuPlacement={"auto"}
                    automation-id={automationId}
                    aria-label={ariaLabel}
                    classNamePrefix={"react-select"}
                    options={options}
                    onChange={onChange as any}
                    menuPortalTarget={document.getElementById("react-select-portal")}
                    value={selectedOption as any}
                    title={selectedOption?.label}
                    placeholder={placeholder}
                    isLoading={isLoading}
                    isSearchable={!!isSearchable}
                    isClearable={!!isClearable}
                    isDisabled={disabled}
                    autocomplete="off"
                    components={components}
                    noOptionsMessage={noOptionsMessage}
                />
            </div>
            {afterInputIcon && afterInputIconOnClick && (
                <div>
                    <ClickableIcon controlId={controlId} disabled={afterInputIconDisabled} icon={afterInputIcon} onClick={afterInputIconOnClick} className={styles.afterInputIcon} />
                </div>
            )}
            {afterInputElement ? afterInputElement : null}
        </Form.Group>
    );
};